// Shared helper for migrating legacy floating-contact config (phoneEnabled,
// emailEnabled, formEnabled, ...) to the new flexible `items` array.
//
// The new structure (`config.settings.floatingContact.items`) is an ordered
// list of items, each item is one of types: 'phone' | 'email' | 'chat' | 'link'.
// Each item has: { id, type, enabled, label, subLabel, value, iconBg, iconColor,
//                  linkType?, pageSlug?, blockId? }   (linkType/pageSlug/blockId
// are only used by 'link' items)

let counter = 0;
const newId = () => `fci_${Date.now()}_${++counter}`;

export const ITEM_DEFAULTS = {
  phone: {
    label: 'Zavolejte nám',
    subLabel: '',
    iconBg: '#dcfce7',
    iconColor: '#16a34a',
  },
  email: {
    label: 'Napište email',
    subLabel: '',
    iconBg: '#dbeafe',
    iconColor: '#2563eb',
  },
  chat: {
    label: 'Živý chat',
    subLabel: 'Napište nám přímo',
    iconBg: '#fef3c7',
    iconColor: '#d97706',
  },
  link: {
    label: 'Kontaktní formulář',
    subLabel: 'Vyplňte formulář',
    iconBg: '#e0e7ff',
    iconColor: '#4f46e5',
    linkType: 'custom',
    pageSlug: '/',
    blockId: '',
  },
};

export const createItem = (type, partial = {}) => ({
  id: newId(),
  type,
  enabled: true,
  ...ITEM_DEFAULTS[type],
  ...partial,
});

/**
 * Returns the items array. If `value.items` is missing or empty, builds it
 * from the legacy individual fields (phoneEnabled/emailEnabled/...) so users
 * never see an empty list after the upgrade.
 */
export const getFloatingItems = (value = {}) => {
  if (Array.isArray(value.items) && value.items.length > 0) {
    return value.items;
  }
  const items = [];
  if (value.phoneEnabled !== false && value.phoneNumber) {
    items.push(createItem('phone', {
      enabled: value.phoneEnabled !== false,
      label: value.phoneLabel || ITEM_DEFAULTS.phone.label,
      subLabel: value.phoneNumber || '',
      iconBg: value.phoneIconBg || ITEM_DEFAULTS.phone.iconBg,
      iconColor: value.phoneIconColor || ITEM_DEFAULTS.phone.iconColor,
    }));
  }
  if (value.emailEnabled !== false && value.emailAddress) {
    items.push(createItem('email', {
      enabled: value.emailEnabled !== false,
      label: value.emailLabel || ITEM_DEFAULTS.email.label,
      subLabel: value.emailAddress || '',
      iconBg: value.emailIconBg || ITEM_DEFAULTS.email.iconBg,
      iconColor: value.emailIconColor || ITEM_DEFAULTS.email.iconColor,
    }));
  }
  // Chat lives in its own item so admin can position it freely; auto-add it
  // only if chat colors were already customised (i.e. user touched the chat
  // panel before). Otherwise it appears as a manual "Add" choice.
  if (value.chatLabel || value.chatSubLabel || value.chatIconBg || value.chatIconColor) {
    items.push(createItem('chat', {
      enabled: true,
      label: value.chatLabel || ITEM_DEFAULTS.chat.label,
      subLabel: value.chatSubLabel || ITEM_DEFAULTS.chat.subLabel,
      iconBg: value.chatIconBg || ITEM_DEFAULTS.chat.iconBg,
      iconColor: value.chatIconColor || ITEM_DEFAULTS.chat.iconColor,
    }));
  }
  if (value.formEnabled !== false && (value.formAnchor || value.formBlockId || value.formPageSlug)) {
    items.push(createItem('link', {
      enabled: value.formEnabled !== false,
      label: value.formLabel || ITEM_DEFAULTS.link.label,
      subLabel: value.formSubLabel || ITEM_DEFAULTS.link.subLabel,
      iconBg: value.formIconBg || ITEM_DEFAULTS.link.iconBg,
      iconColor: value.formIconColor || ITEM_DEFAULTS.link.iconColor,
      linkType: value.formLinkType || 'custom',
      pageSlug: value.formPageSlug || '',
      blockId: value.formBlockId || '',
      value: value.formAnchor || '',
    }));
  }
  return items;
};

/**
 * Resolve the href for a 'link' item.
 */
export const resolveLinkHref = (item) => {
  if (!item) return '';
  if (item.linkType === 'block' && item.blockId) {
    const slug = item.pageSlug || '/';
    if (slug === '/' || !slug) return `/#${item.blockId}`;
    const s = slug.startsWith('/') ? slug : `/${slug}`;
    return `${s}#${item.blockId}`;
  }
  if (item.linkType === 'page' && item.pageSlug) {
    const slug = item.pageSlug;
    return slug.startsWith('/') ? slug : `/${slug}`;
  }
  const raw = (item.value || '').trim();
  if (!raw) return '';
  if (!/^([a-zA-Z][a-zA-Z0-9+\-.]*:\/\/|#|\/|mailto:|tel:)/.test(raw) && /\.[a-z]{2,}/i.test(raw)) {
    return `https://${raw}`;
  }
  return raw;
};
